"use client";

import { useMutation, useQuery } from "@apollo/client";
import { ESystemRoles } from "@constants/SystemRoles";
import { MaintainerDataTableV2 } from "components/ui/v2/maintainer/maintainer-data-table-v2";
import { IColumn } from "components/ui/v2/maintainer/types";
import {
  Avatar,
  Dialog,
  LiquidPill,
  LiquidPillDropdown,
  MaintainerFabStackV2,
  ListPageShell,
  Modal,
  ModalButtons,
  PageEmptyStateV2,
} from "components/ui/v2";
import type { LiquidPillDropdownItem } from "components/ui/v2/liquid-pill-dropdown";
import { useUserStoreContext } from "context/userStore/userStore.provider";
import { gqlCertificate } from "gql";
import { ICreateCertificateInput } from "gql/Certificates/mutations";
import { ICertificate, IGetAllCertificate } from "gql/Certificates/queries";
import {
  AlertCircle,
  CheckCircle,
  Eye,
  Medal,
  MoreHorizontal,
  Pencil,
  RefreshCw,
  Trash2,
  Users,
} from "lucide-react";
import useTranslation from "next-translate/useTranslation";
import { type ReactNode, useMemo, useState } from "react";
import { toast } from "react-hot-toast";
import { getQueryOperator } from "utils/helpers";
import {
  CERTIFICATE_FORM_ID,
  CertificateCreateModalContentV2,
} from "./CertificateCreateModalContentV2";
import { CertificateUpdateModalContentV2 } from "./CertificateUpdateModalContentV2";
import {
  ASSIGN_CERTIFICATE_EMAIL_FORM_ID,
  ASSIGN_CERTIFICATE_TEAM_FORM_ID,
  AssignCertificateModalContentV2,
} from "./AssignCertificateModalContentV2";
import { CertificatesLoadingStateV2 } from "./CertificatesLoadingStateV2";
import { PreviewCertificateModalV2 } from "./PreviewCertificateModalV2";
import { UsersAssignationModalContentV2 } from "./UsersAssignationModalContentV2";

export function CertificatesMaintainerPageV2() {
  const { t } = useTranslation("common");
  const { user } = useUserStoreContext((store) => ({ user: store.user }));

  const [search, setSearch] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<ICertificate | null>(null);
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignCertificate, setAssignCertificate] = useState<ICertificate | null>(
    null
  );
  const [assignTab, setAssignTab] = useState<"email" | "team">("email");
  const [assignLoading, setAssignLoading] = useState(false);
  const [createFooter, setCreateFooter] = useState<ReactNode>(null);
  const [editFooter, setEditFooter] = useState<ReactNode>(null);
  const [assignmentsOpen, setAssignmentsOpen] = useState(false);
  const [assignmentsCertificate, setAssignmentsCertificate] =
    useState<ICertificate | null>(null);
  const [assignmentsContentLoading, setAssignmentsContentLoading] =
    useState(false);

  const { loading, data, error, refetch } = useQuery<IGetAllCertificate>(
    gqlCertificate.queries.GET_CERTIFICATES,
    { fetchPolicy: "network-only", notifyOnNetworkStatusChange: true }
  );

  const allCertificates = data?.getAllCertificate ?? [];
  const hasLoadedData = data != null;
  const isQueryError = Boolean(error) && !hasLoadedData;
  const certificates = useMemo(() => {
    if (!search.trim()) return allCertificates;
    return allCertificates.filter((item) =>
      item.name.toLowerCase().includes(search.toLowerCase())
    );
  }, [allCertificates, search]);

  const totalAssignments = useMemo(
    () =>
      allCertificates.reduce(
        (sum, cert) => sum + (cert.totalCertificatedUsers || 0),
        0
      ),
    [allCertificates]
  );

  const withAssignmentsCount = useMemo(
    () =>
      allCertificates.filter((cert) => (cert.totalCertificatedUsers || 0) > 0)
        .length,
    [allCertificates]
  );

  const [deletedCertificate] = useMutation<{
    certificateInput: ICreateCertificateInput;
  }>(gqlCertificate.mutations.DELETE_CERTIFICATE, {
    awaitRefetchQueries: true,
    refetchQueries: [getQueryOperator(gqlCertificate.queries.GET_CERTIFICATES)],
    onCompleted() {
      toast.success(t("Certificado eliminado con exito"));
    },
    onError(e) {
      toast.error(e.message);
    },
  });

  const confirmDelete = async () => {
    if (!selectedItem) return;
    setDeleteOpen(false);
    await deletedCertificate({
      variables: { deleteCertificateId: selectedItem.id },
    });
    setSelectedItem(null);
  };

  const getActionItems = (rowData: ICertificate) => {
    const items: LiquidPillDropdownItem[] = [
      { value: "preview", label: t("Previsualizar"), icon: Eye, variant: "indigo" },
      { value: "edit", label: t("Editar"), icon: Pencil, variant: "indigo" },
      { value: "assign", label: t("Asignar"), icon: Medal, variant: "emerald" },
      {
        value: "assignments",
        label: t("Ver asignaciones"),
        icon: Users,
        variant: "sky",
      },
    ];

    if (user?.isi || user?.systemRole === ESystemRoles.ADMIN) {
      items.push({
        value: "delete",
        label: t("Eliminar"),
        icon: Trash2,
        variant: "rose",
      });
    }

    return items;
  };

  const handleActionSelect = (value: string, rowData: ICertificate) => {
    switch (value) {
      case "preview":
        setSelectedItem(rowData);
        setPreviewOpen(true);
        break;
      case "edit":
        setSelectedItem(rowData);
        setEditOpen(true);
        break;
      case "assign":
        setAssignCertificate(rowData);
        setAssignTab("email");
        setAssignOpen(true);
        break;
      case "assignments":
        setAssignmentsContentLoading(true);
        setAssignmentsCertificate(rowData);
        setAssignmentsOpen(true);
        break;
      case "delete":
        setSelectedItem(rowData);
        setDeleteOpen(true);
        break;
    }
  };

  const handleAssignmentsLoadingChange = (isLoading: boolean) => {
    if (!isLoading) setAssignmentsContentLoading(false);
  };

  const col: IColumn<ICertificate>[] = [
    {
      dataKey: "customImage",
      header: t("Imagen"),
      width: 86,
      customCell: ({ rowData }) => (
        <Avatar
          name={rowData?.name || t("Certificado")}
          src={rowData?.customImage || undefined}
          size="md"
        />
      ),
    },
    { dataKey: "name", header: t("Nombre"), width: 300 },
    { dataKey: "durationText", header: t("Duracion"), width: 100 },
    {
      dataKey: "startDate",
      header: t("Fecha Inicio"),
      width: 120,
      customCell: ({ rowData }) => {
        const date = rowData.startDate ? new Date(rowData.startDate) : null;
        const formattedDate = date
          ? `${date.getDate().toString().padStart(2, "0")}/${(date.getMonth() + 1).toString().padStart(2, "0")}/${date.getFullYear()}`
          : t("Fecha no disponible");
        return <span>{formattedDate}</span>;
      },
    },
    {
      dataKey: "totalCertificatedUsers",
      header: t("Asignaciones"),
      width: 180,
      customCell: ({ rowData }) => (
        <LiquidPill
          variant="violet"
          appearance="tinted"
          shadow="neutral"
          size="xs"
          icon={<Users className="h-3 w-3" />}
          text={
            <span className="font-semibold text-current">
              {rowData.totalCertificatedUsers || 0}
            </span>
          }
        />
      ),
    },
    {
      dataKey: "id",
      header: t("Opciones"),
      width: 92,
      fixed: "right",
      customCell: ({ rowData }) => (
        <LiquidPillDropdown
          items={getActionItems(rowData)}
          onSelect={(value) => handleActionSelect(value as string, rowData)}
          icon={<MoreHorizontal className="h-4 w-4" />}
          size="sm"
          collapsedShape="circle"
          reactive
          variant="slate"
          aria-label={t("Opciones")}
        />
      ),
    },
  ];

  const showListLoading = loading && !hasLoadedData;
  const isEmpty =
    !showListLoading && (isQueryError || certificates.length === 0);

  return (
    <ListPageShell
      header={{
        leadingIcon: <Medal className="h-10 w-10 shrink-0" />,
        title: t("Certificados"),
        description: t(
          "Crea, edita y elimina los certificados de tu organización"
        ),
        badges: (
          <>
            <LiquidPill
              variant="violet"
              appearance="tinted"
              shadow="neutral"
              size="sm"
              icon={<Medal className="h-4 w-4" />}
              text={
                <>
                  <span className="font-semibold text-current">
                    {allCertificates.length}
                  </span>{" "}
                  {t("Certificados")}
                </>
              }
            />
            <LiquidPill
              variant="violet"
              appearance="tinted"
              shadow="neutral"
              size="sm"
              icon={<CheckCircle className="h-4 w-4" />}
              text={
                <>
                  <span className="font-semibold text-current">
                    {withAssignmentsCount}
                  </span>{" "}
                  {t("Con asignaciones")}
                </>
              }
            />
            <LiquidPill
              variant="violet"
              appearance="tinted"
              shadow="neutral"
              size="sm"
              icon={<Users className="h-4 w-4" />}
              text={
                <>
                  <span className="font-semibold text-current">
                    {totalAssignments}
                  </span>{" "}
                  {t("Asignaciones")}
                </>
              }
            />
          </>
        ),
        showParticles: true,
        particleIcon: Medal,
      }}
      searchValue={search}
      onSearchChange={setSearch}
      searchPlaceholder={t("Buscar certificado")}
      loading={showListLoading}
      loadingState={
        <CertificatesLoadingStateV2
          title={t("Cargando certificados")}
          description={t("Preparando el listado de certificados")}
        />
      }
      isEmpty={isEmpty}
      emptyState={
        isQueryError ? (
          <PageEmptyStateV2
            icon={<AlertCircle className="h-8 w-8" />}
            title={t("No se pudieron cargar los certificados")}
            description={t(
              "Ocurrió un error al obtener el listado. Intenta de nuevo."
            )}
            actions={
              <LiquidPill
                as="button"
                type="button"
                variant="violet"
                appearance="tinted"
                size="md"
                icon={<RefreshCw className="h-3.5 w-3.5" />}
                text={t("Intentar de nuevo")}
                aria-label={t("Intentar de nuevo")}
                onClick={() => {
                  void refetch();
                }}
              />
            }
          />
        ) : (
          <PageEmptyStateV2
            variant={search ? "noResult" : "empty"}
            icon={<Medal className="h-8 w-8" />}
            title={
              search
                ? t("No se encontraron certificados")
                : t("No hay certificados aún")
            }
            onClearSearch={search ? () => setSearch("") : undefined}
            clearSearchLabel={t("Limpiar búsqueda")}
          />
        )
      }
      metaIcon={Medal}
      metaSummary={
        certificates.length > 0
          ? `${t("Mostrando")} ${certificates.length} ${t("de")} ${allCertificates.length} ${t("certificados")}`
          : undefined
      }
      fab={
        <MaintainerFabStackV2
          createAriaLabel={t("Crear Certificado")}
          onCreate={() => setCreateOpen(true)}
          createVariant="violet"
        />
      }
      overlays={
        <>
          <Dialog
            open={deleteOpen}
            onClose={() => {
              setDeleteOpen(false);
              setSelectedItem(null);
            }}
            title={t("¿Estás seguro?")}
            description={t("Estas a punto de eliminar el certificado", {
              certificateName: selectedItem?.name ?? "",
            })}
            size="sm"
            footer={
              <ModalButtons
                compact
                onCancel={() => {
                  setDeleteOpen(false);
                  setSelectedItem(null);
                }}
                submitLabel={t("Eliminar")}
                submitVariant="destructive"
                onSubmit={confirmDelete}
              />
            }
          >
            {null}
          </Dialog>

          <PreviewCertificateModalV2
            open={previewOpen && Boolean(selectedItem)}
            onClose={() => {
              setPreviewOpen(false);
              setSelectedItem(null);
            }}
            certificate={
              selectedItem
                ? {
                    name: selectedItem.name,
                    duration: selectedItem.durationText,
                    subtitle: selectedItem.subTitle,
                    badge: selectedItem.customImage,
                    certificateLabel: selectedItem.certificateLabel,
                    vendorImage: selectedItem.overrideVendorImage,
                    userName: user?.name,
                    userLastName: user?.lastname,
                    certificateTopLabel: selectedItem.certificateTopLabel,
                    preview: true,
                  }
                : null
            }
          />

          <Modal
            open={createOpen}
            onClose={() => {
              setCreateOpen(false);
              setCreateFooter(null);
            }}
            title={t("Agregar Certificado")}
            size="md"
            panelClassName="max-h-[min(88vh,680px)]"
            headerClassName="px-4 py-2.5 sm:px-5 sm:py-3"
            bodyClassName="px-4 py-3 sm:px-5 sm:py-3.5"
            footer={createFooter}
          >
            <CertificateCreateModalContentV2
              onClose={() => {
                setCreateOpen(false);
                setCreateFooter(null);
              }}
              hideHeader
              hideFooter
              formId={CERTIFICATE_FORM_ID}
              onFooterChange={setCreateFooter}
            />
          </Modal>

          <Modal
            open={editOpen && Boolean(selectedItem)}
            onClose={() => {
              setEditOpen(false);
              setSelectedItem(null);
              setEditFooter(null);
            }}
            title={t("Actualizar Certificado")}
            size="md"
            panelClassName="max-h-[min(88vh,680px)]"
            headerClassName="px-4 py-2.5 sm:px-5 sm:py-3"
            bodyClassName="px-4 py-3 sm:px-5 sm:py-3.5"
            footer={editFooter}
          >
            {selectedItem && (
              <CertificateUpdateModalContentV2
                certificate={selectedItem}
                onClose={() => {
                  setEditOpen(false);
                  setSelectedItem(null);
                  setEditFooter(null);
                }}
                hideHeader
                hideFooter
                formId={CERTIFICATE_FORM_ID}
                onFooterChange={setEditFooter}
              />
            )}
          </Modal>

          <Modal
            open={assignOpen && Boolean(assignCertificate)}
            onClose={() => {
              setAssignOpen(false);
              setAssignCertificate(null);
              setAssignLoading(false);
            }}
            title={t("Asignar certificado")}
            size="md"
            panelClassName="max-h-[min(88vh,680px)]"
            headerClassName="px-4 py-2.5 sm:px-5 sm:py-3"
            bodyClassName="px-4 py-3 sm:px-5 sm:py-3.5"
            footer={
              <ModalButtons
                compact
                loading={assignLoading}
                onCancel={() => {
                  setAssignOpen(false);
                  setAssignCertificate(null);
                }}
                submitLabel={t("Enviar")}
                submitButtonProps={{
                  form:
                    assignTab === "email"
                      ? ASSIGN_CERTIFICATE_EMAIL_FORM_ID
                      : ASSIGN_CERTIFICATE_TEAM_FORM_ID,
                }}
              />
            }
          >
            {assignCertificate ? (
              <AssignCertificateModalContentV2
                certificate={assignCertificate}
                hideHeader
                hideFooter
                activeTab={assignTab}
                onTabChange={setAssignTab}
                onLoadingChange={setAssignLoading}
                onClose={() => {
                  setAssignOpen(false);
                  setAssignCertificate(null);
                }}
              />
            ) : null}
          </Modal>

          <Modal
            open={assignmentsOpen && Boolean(assignmentsCertificate)}
            onClose={() => {
              setAssignmentsOpen(false);
              setAssignmentsCertificate(null);
            }}
            title={t("Certificados Asignados")}
            size="xl"
            panelClassName="flex max-h-[min(88vh,780px)] flex-col"
            headerClassName="px-4 py-2.5 sm:px-5 sm:py-3"
            bodyClassName="bg-gradient-to-b from-transparent via-white/95 via-[10%] to-white/95 to-[16%] py-3 dark:via-zinc-950/95 dark:to-zinc-950/95 sm:py-3.5"
            bodyScrollFade
            loading={assignmentsContentLoading}
            bubbleReveal
            loadingLabel={t("Cargando asignaciones")}
          >
            {assignmentsCertificate ? (
              <UsersAssignationModalContentV2
                certificate={assignmentsCertificate}
                hideHeader
                onLoadingChange={handleAssignmentsLoadingChange}
              />
            ) : null}
          </Modal>
        </>
      }
    >
      <MaintainerDataTableV2<ICertificate>
        data={certificates}
        columns={col}
        resetOnNewData={false}
      />
    </ListPageShell>
  );
}
